import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useState } from "react";

import { Button, Dropdown, FormField, Input, Popup, Radio } from "semantic-ui-react";

import { RowInfo } from "react-table-6";

import SearchBoxDropdown, { SelectedItemHandler } from "react-lib/appcon/common/SearchBoxDropdown";

import CardEditMedOrderUX from "./CardEditMedOrderUX";
import ModDrugDispensingOrder from "./ModDrugDispensingOrder";
import ModDrugFact from "./ModDrugFact";
import ModPediatricDose from "./ModPediatricDose";
import { TaperPopup } from "./SubDrugOrderStatusAlert";
import DrugSearchBox, { DrugSearchBoxProps } from "./DrugSearchBox";

import { DRUG_ORDER_TYPE } from "./TPDInterface";
import { useIntl } from "react-intl";

import CONFIG from "config/config";

// Types
type CardEditMedOrderProps = Pick<
  DrugSearchBoxProps,
  "drugSearchLoading" | "drugSearchResult" | "drugSearchText" | "selectedDrug"
> & {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => any) => any;

  DrugSelectSequence: any;

  drugDescription?: string;
  drugDose?: string;
  drugDuration?: string;
  drugOrder?: Record<string, any>;
  drugPermission?: Record<string, any> | null;
  drugPrn?: string;
  drugQty?: string;
  hideDisplayData?: boolean;
  languageUX?: string;
  modSolventSelectionConfig?: Record<string, any> | null;
  openCalculator: boolean;
  orderType?: string;
  orderTypeTab?: string;
  pediatricData: any;
  selectedAppointment?: Record<string, any> | null;
  showHeader?: boolean;
  statusRequested: boolean;
  errorMessage: any;

  searchedItemListWithKey?: Record<string, any>;

  masterData?: Record<string, any>;
  masterOptions?: Record<string, any>;

  setErrorMessage: (value: any) => void;
  setOpenCalculator: (value: boolean) => void;
  onAddToOrder: () => void;
  onClose?: () => void;
};

const CARD_DRUG_ORDER_ID = "CardDrugOrder";

export const INITIAL_SOLVENT_DATA = {
  quantity: null,
  selectedSolvent: null,
  solvent: null,
  unit: null,
  volume: null,
};

export const INITIAL_SOLVENT_ADR = {
  solventAdrNotes: "",
  solvent_note_adverse_reaction: "",
};

// Mod Solvent Selection
export const prepareModSolventSelection = ({
  drug,
  drugPermission,
  frequency,
  noDefaultSolvent = false,
  orderType,
  solvent,
}: {
  drug: any;
  drugPermission: any;
  frequency?: any[];
  noDefaultSolvent?: boolean;
  orderType?: string;
  solvent?: Record<string, any> | null;
}) => {
  const frequencyId = orderType === "CONTINUE_PLAN" ? drug.frequency : drug.selectedDrugFrequencyId;

  const foundFrequency = (frequency || []).find((item) => item.id === frequencyId);

  let quantity = solvent?.quantity || drug.default_solvent_quantity;

  if (!(noDefaultSolvent || quantity) && drug.default_solvent) {
    quantity = foundFrequency?.dose_per_day;
  }

  const admixtureOptions: any = {
    // concentration_unit: drugSelectSeq?.drug_concentration_unit,
    drug_drip_in: drug?.drug_drip_in,
    drug_drip_in_unit: drug?.drug_drip_in_unit,
    mixture_iv_rate: drug?.mixture_iv_rate,
    mixture_iv_rate_unit: drug?.mixture_iv_rate_unit,
    // quantity: foundFrequency?.dose_per_day || 1,
    // solute_concentration_rate: drugSelectSeq?.mixture_concentration_solute,
    // solvent_concentration_rate: drugSelectSeq?.mixture_concentration_solvent,
    // strength: drugSelectSeq?.dose || 1,
    // strength_unit: drugSelectSeq?.unit || "",
    // volume: drugSelectSeq?.mixture_solvent_volume,
    ...(!noDefaultSolvent &&
      solvent && {
        drug_drip_in: solvent.drug_drip_in,
        drug_drip_in_unit: solvent.drug_drip_in_unit,
        mixture_iv_rate: solvent.mixture_iv_rate,
        mixture_iv_rate_unit: solvent.mixture_iv_rate_unit,
        quantity,
        unit: solvent.unit || drug.default_volume_unit,
        volume: solvent.volume || Number(drug.default_volume_for_mixing) || "",
      }),
  };

  if (
    drug?.add_auto_solvent &&
    drug?.default_solvent &&
    drugPermission?.config_TPD_ENABLE_IV_COMPATIBLE
  ) {
    // admixtureOptions.volume = drugSelectSeq?.stock_size || 1;
    // #admixtureOptions.quantity = foundFrequency?.dose_per_day || 1;
    // admixtureOptions.mixture_iv_rate = drugSelectSeq?.mixture_iv_rate || "";
    // admixtureOptions.mixture_iv_rate_unit = drugSelectSeq?.mixture_iv_rate_unit || "";
    // #admixtureOptions.solute_concentration_rate = drugSelectSeq?.solute_concentration_rate || "";
    // #admixtureOptions.solvent_concentration_rate = drugSelectSeq?.solvent_concentration_rate || "";
    // #admixtureOptions.concentration_unit = drugSelectSeq?.concentration_unit || "";
    // admixtureOptions.drug_drip_in = drugSelectSeq?.drug_drip_in || "";
    // admixtureOptions.drug_drip_in_unit = drugSelectSeq?.drug_drip_in_unit || "";
  }

  const params = {
    admixtureOptions,
    drugOrderType: orderType,
    priority: drug.priority,
    skipSolventHandling: solvent?.solvent,
    // solventId: drug?.default_solvent,
    // solventId: drugSelectSeq?.product || data?.solvent,
  };

  return {
    admixtureOptions,
    params,
  };
};

export const getPrnOptions = (
  options: Record<string, any>[] = [],
  prn = ""
): Record<string, any>[] =>
  // สร้างและส่งคืน prnOptions
  [...options, { key: prn, text: prn, value: prn }]
    .map((option) => ({
      ...option,
      value: option.text,
    }))
    .filter((option) => option.value);

const CardEditMedOrder = (props: CardEditMedOrderProps) => {
  const intl = useIntl();
  const [openModDispense, setOpenModDispense] = useState<boolean>(false);
  // factsheet
  const [openModFactSheet, setOpenModFactSheet] = useState<boolean>(false);

  const [prnOptions, setPrnOptions] = useState<Record<string, any>[]>([]);

  // Memo Effect
  const displayData = useMemo(
    () =>
      props.hideDisplayData
        ? {
            DrugSelectSequence: {} as any,
            drugDescription: "",
            drugDose: "",
            drugDuration: "",
            drugQty: "",
            drugSearchText: "",
            modSolventSelectionConfig: {} as any,
            searchedItemListWithKey: {},
            selectedDrug: {} as any,
          }
        : {
            DrugSelectSequence: props.DrugSelectSequence,
            drugDescription: props.drugDescription,
            drugDose: props.drugDose,
            drugDuration: props.drugDuration,
            drugQty: props.drugQty,
            drugSearchText: props.drugSearchText,
            modSolventSelectionConfig: props.modSolventSelectionConfig,
            searchedItemListWithKey: props.searchedItemListWithKey,
            selectedDrug: props.selectedDrug,
          },
    [props]
  );

  const isRequireRate = useMemo(() => {
    const route: any[] = props.masterData?.route || [];

    const foundRoute = route.find(
      (item) => item.id === displayData.DrugSelectSequence?.selectedDrugRouteId
    );

    return !!foundRoute?.require_rate;
  }, [displayData.DrugSelectSequence, props.masterData?.route]);

  const isEditable = useMemo(
    () => typeof displayData.modSolventSelectionConfig?.admixtureIndex === "number",
    [displayData.modSolventSelectionConfig?.admixtureIndex]
  );

  // * Default ค่า solvent เมื่อ require rate
  useEffect(() => {
    if (!isRequireRate) {
      return;
    }

    const { params } = prepareModSolventSelection({
      drug: displayData.DrugSelectSequence,
      drugPermission: props.drugPermission,
      frequency: props.masterData?.frequency,
      noDefaultSolvent: !displayData.modSolventSelectionConfig?.solvent,
      orderType: props.orderType,
      solvent: displayData.modSolventSelectionConfig,
    });

    props.onEvent({
      message: "initModSolventSelection",
      params,
    });
  }, [isRequireRate]);

  useEffect(() => {
    setPrnOptions(getPrnOptions(props.masterOptions?.prn, props.drugPrn));
  }, [props.drugPrn, props.masterOptions?.prn]);

  useEffect(() => {
    if (
      displayData.selectedDrug?.id &&
      !props.showHeader &&
      displayData.selectedDrug?.original?.add_auto_solvent &&
      displayData.modSolventSelectionConfig?.showButtonSolvent
    ) {
      handleOpenModSolventSelection();
    }
  }, [
    displayData.modSolventSelectionConfig?.showButtonSolvent,
    displayData.selectedDrug?.original,
  ]);

  const enabledCalculatorButton = useMemo(() => {
    const items: any[] = props.pediatricData?.items || [];

    return items.length > 0;
  }, [props.pediatricData]);

  const hideDrugForm = useMemo(() => {
    const checkRequire = (requireKey: string) => {
      const required: boolean | undefined =
        displayData.DrugSelectSequence?.[`require_${requireKey}`];

      return !required && required !== undefined;
    };

    return {
      dose: checkRequire("unit"),
      duration: [
        "ONE_DOSE_IPD",
        "ONE_DOSE_OPD",
        props.orderTypeTab === DRUG_ORDER_TYPE.DAILY ? "ONE_DAY_IPD" : "",
        "STAT_IPD",
        "STAT_OPD",
      ]
        .filter(Boolean)
        .includes(props.orderType || ""),
      frequency: checkRequire("frequency"),
      method: checkRequire("method"),
      route: checkRequire("route"),
      site: checkRequire("site"),
      unit: checkRequire("unit"),
    };
  }, [displayData.DrugSelectSequence, props.orderType, props.orderTypeTab, props.selectedDrug]);

  const errorPediatricData = useMemo(() => {
    const error: any[] | undefined = props.pediatricData?.selectedItem?.error;

    return Array.isArray(error) ? error : null;
  }, [props.pediatricData]);

  const priority = useMemo(() => {
    const drugPriority: string = displayData.DrugSelectSequence?.priority || "";

    return drugPriority;
  }, [displayData.DrugSelectSequence?.priority]);

  const priorityOptions = useMemo(() => {
    const options = props.onEvent({
      message: "GetDrugPriorityOptions",
      params: {},
    });

    return options as Record<string, any>[];
  }, [props.orderType]);

  const frequencyOptions = useMemo(() => {
    const options: Record<string, any>[] = displayData.DrugSelectSequence?.drugFrequencies || [];

    return options;
  }, [displayData.DrugSelectSequence?.drugFrequencies, props.masterData?.frequency]);

  const drugDispensingFrequency = useMemo(() => {
    const frequencyId: number = displayData.DrugSelectSequence?.default_frequency;

    const drugFrequencies: any[] = props.masterOptions?.frequency || [];

    if (props.orderTypeTab === DRUG_ORDER_TYPE.DAILY && props.orderType?.includes("STAT")) {
      const foundFrequency = drugFrequencies.find((option) =>
        (option.text as string).includes("STAT")
      );

      return (foundFrequency?.value || null) as number | null;
    }

    return frequencyId;
  }, [displayData.DrugSelectSequence?.default_frequency, props.masterOptions?.frequency]);

  const handleSelectDrug = (rowInfo: RowInfo) => {
    const selectedDrug = props.drugSearchResult?.[rowInfo.index];

    if (!CONFIG.HIDE_DOSE_CALCULATOR) {
      props.onEvent({
        message: "GetPediatricDose",
        params: {
          action: "LIST",
          card: "ModPediatricDose",
          drugDetail: selectedDrug,
        },
      });
    }

    props.onEvent({
      message: "RunSequence",
      params: {
        sequence: "DrugSelect",
        card: CARD_DRUG_ORDER_ID,
        orderType: props.orderType,
        patient: props.selectedAppointment?.patient,
        selectedDrug: selectedDrug,
      },
    });
  };

  const handleOpenModSolventSelection = async () => {
    const data = displayData.modSolventSelectionConfig;
    const active = data?.activeButtonSolvent;
    const isEdit = typeof data?.admixtureIndex === "number";

    if (active || isEdit) {
      return;
    }

    await props.onEvent({
      message: "SetModSolventSelectionConfig",
      params: { activeButtonSolvent: true, editable: true, editableQuantity: true },
    });

    const { params } = prepareModSolventSelection({
      drug: displayData.DrugSelectSequence,
      drugPermission: props.drugPermission,
      frequency: props.masterData?.frequency,
      orderType: props.orderType,
      solvent: displayData.modSolventSelectionConfig,
    });

    props.onEvent({
      message: "initModSolventSelection",
      params,
    });
  };

  const handleChangeDate = (date: string) => {
    props.setProp("DrugSelectSequence.selectedDate", date);
  };

  const handleCloseModDispense = () => {
    setOpenModDispense(false);
  };

  const handleSaveDrugDispense = async (data: any) => {
    props.setProp("drugDose", data.dose);
    props.setProp("drugQty", data.quantity);

    await props.setProp("DrugSelectSequence", {
      ...displayData.DrugSelectSequence,
      isDrugDispense: true,
      selectedDrugFrequencyId: data.frequency,
      selectedDrugRouteId: data.route,
      selectedDrugUnitId: data.unit,
    });

    props.onEvent({
      message: "AddToOrder",
      params: {
        card: CARD_DRUG_ORDER_ID,
        isCheckDrugGroup: true,
        orderType: props.orderType,
        onFailed: props.setErrorMessage,
      },
    });

    handleCloseModDispense();
  };

  const handleClearDrugOrderForm = () => {
    props.setErrorMessage(null);

    props.onEvent({ message: "ClearDrugOrderForm", params: {} });

    props.setOpenCalculator(false);
  };

  const validateValue = (value: string) => {
    if (value === "") {
      return "";
    }

    return +value < 0 ? "0" : value;
  };

  const handleBlurDrugDose = (e: any) => {
    const { value } = e.target;

    props.onEvent({
      message: "HandleChangeDrugOrderDetail",
      params: {
        card: CARD_DRUG_ORDER_ID,
        key: "drugDose",
        orderType: props.orderTypeTab,
        value: validateValue(value),
      },
    });
  };

  const handleChangeDrugDose = (e: any) => {
    const { value } = e.target;

    props.setProp("drugDose", validateValue(value));
  };

  const handleChangeDrugUnit = (e: any, data: any) => {
    props.onEvent({
      message: "HandleChangeDrugOrderDetail",
      params: {
        card: CARD_DRUG_ORDER_ID,
        key: "selectedDrugUnitId",
        orderType: props.orderTypeTab,
        stateKey: "DrugSelectSequence",
        value: data.value,
      },
    });
  };

  const onClickDispense = () => {
    setOpenModDispense(true);
  };

  const handleChangeDrugPrn = (e: any, data: any) => {
    props.setProp("drugPrn", data.value);
  };

  const handleOpenFactSheet = () => {
    setOpenModFactSheet(true);
  };

  const handleCloseModFactSheet = () => {
    setOpenModFactSheet(false);
  };

  const handleChangeDrugDuration = (e: any) => {
    props.onEvent({
      message: "HandleChangeDrugOrderDetail",
      params: {
        card: CARD_DRUG_ORDER_ID,
        key: "drugDuration",
        orderType: props.orderTypeTab,
        value: e.target.value,
      },
    });
  };

  const handleChangeDrugFrequency = (e: any, data: any) => {
    if (
      displayData.modSolventSelectionConfig?.activeButtonSolvent &&
      displayData.modSolventSelectionConfig?.solvent
    ) {
      const frequency: any[] = props.masterData?.frequency || [];

      const foundFrequency = frequency.find((item) => item.id === data.value);

      props.setProp("modSolventSelectionConfig", {
        ...props.modSolventSelectionConfig,
        quantity: foundFrequency?.dose_per_day,
      });
    }

    props.onEvent({
      message: "HandleChangeDrugOrderDetail",
      params: {
        card: CARD_DRUG_ORDER_ID,
        key: "selectedDrugFrequencyId",
        orderType: props.orderTypeTab,
        stateKey: "DrugSelectSequence",
        value: data.value,
      },
    });
  };

  const handleChangeDrugQty = (e: any) => {
    // Todo: validate number
    props.onEvent({
      message: "HandleChangeDrugOrderDetail",
      params: {
        card: CARD_DRUG_ORDER_ID,
        key: "drugQty",
        orderType: props.orderTypeTab,
        value: e.target.value,
      },
    });
  };

  const handleChangeDrugRoute = (e: any, data: any) => {
    const route: any[] = props.masterData?.route || [];

    const foundRoute = route.find((item) => item.id === data.value);

    if (!foundRoute?.require_rate) {
      props.setProp("modSolventSelectionConfig", {
        ...props.modSolventSelectionConfig,
        dripRate: null,
        drug_drip_in: null,
        drug_drip_in_unit: null,
        mixture_iv_rate: null,
        mixture_iv_rate_unit: null,
      });
    }

    props.setProp("DrugSelectSequence", {
      ...displayData.DrugSelectSequence,
      selectedDrugRouteId: data.value,
    });
  };

  const handleChangeDrugSite = (e: any, data: any) => {
    props.setProp("DrugSelectSequence", {
      ...displayData.DrugSelectSequence,
      selectedDrugSiteId: data.value,
    });
  };

  const handleChangePriority = async (e: any, data: any) => {
    const priority = data.value;
    const seq = displayData.DrugSelectSequence || {};
    const drugFrequencies: any[] = props.masterOptions?.frequency || [];

    let frequencyId = seq.default_frequency;

    const orderType: string = props.onEvent({
      message: "GetDrugOrderType",
      params: { orderType: props.orderTypeTab, priority },
    });

    if (orderType.includes("STAT")) {
      frequencyId =
        drugFrequencies.find((option) => (option.text as string).includes("STAT"))?.value || null;
    }

    const foundFrequency = drugFrequencies.find((option) => option.value === frequencyId);

    await props.setProp("DrugSelectSequence", {
      ...seq,
      priority,
      selectedDrugFrequencyId: foundFrequency?.value || "",
    });

    props.onEvent({ message: "SetDrugContinueOptions", params: { drugResp: seq, orderType } });
  };

  const handleChangeDrugDescription = (e: any) => {
    props.onEvent({
      message: "HandleChangeDrugOrderDetail",
      params: {
        card: CARD_DRUG_ORDER_ID,
        key: "drugDescription",
        orderType: props.orderTypeTab,
        value: e.target.value,
      },
    });
  };

  const handleCloseModPediatricDose = () => {
    props.setOpenCalculator(false);
  };

  const handleCloseButtonSolvent = () => {
    props.setProp("DrugSelectSequence", {
      ...displayData.DrugSelectSequence,
      ...INITIAL_SOLVENT_ADR,
    });

    props.setProp("modSolventSelectionConfig", {
      ...props.modSolventSelectionConfig,
      activeButtonSolvent: false,
      ...INITIAL_SOLVENT_DATA,
    });
  };

  // change
  const handleChangeModData = (event: any, data: any) => {
    if (data.type === "number" && Number(data.value) < 0) {
      data.value = "0";
    }

    props.onEvent({
      message: "SetModSolventSelectionData",
      params: { name: data.name, value: data.value },
    });
  };

  const handleSelectedItem: SelectedItemHandler = (value) => {
    if (!value) {
      props.setProp("DrugSelectSequence", {
        ...displayData.DrugSelectSequence,
        ...INITIAL_SOLVENT_ADR,
      });

      props.setProp("modSolventSelectionConfig", {
        ...props.modSolventSelectionConfig,
        ...INITIAL_SOLVENT_DATA,
      });

      return;
    }

    props.onEvent({
      message: "GetDrugSolventDetail",
      params: { id: value },
    });
  };

  const handleAddToOrder = () => {
    props.onEvent({
      message: "SaveSolventData",
      params: {
        drugOrderType: props.orderType,
        isRequireRate,
        onSuccess: () => {
          if (isEditable) {
            props.onClose?.();

            return;
          }

          props.onAddToOrder();
        },
      },
    });
  };

  const handleClose = () => {
    props.onClose?.();

    handleClearDrugOrderForm();
  };

  const handleAddPrn = (e: SyntheticEvent, v: any) => {
    setPrnOptions([...prnOptions, { key: v.value, text: v.value, value: v.value }]);
  };

  return (
    <div>
      <CardEditMedOrderUX
        // ChangeDrugPrn={handleChangeDrugPrn}
        changeDrugDescription={handleChangeDrugDescription}
        changeDrugDuration={handleChangeDrugDuration}
        changeDrugFrequency={handleChangeDrugFrequency}
        ChangeDrugPrn={handleChangeDrugPrn}
        changeDrugQty={handleChangeDrugQty}
        changeDrugRoute={handleChangeDrugRoute}
        changeDrugSite={handleChangeDrugSite}
        changeDrugUnit={handleChangeDrugUnit}
        changePriority={handleChangePriority}
        clearDrugOrderForm={handleClearDrugOrderForm}
        disabledFrequency={priority === "STAT" && props.orderTypeTab === DRUG_ORDER_TYPE.DAILY}
        disabledPriority={props.drugOrder?.id}
        drugDescription={displayData.drugDescription}
        drugDose={displayData.drugDose}
        drugDuration={displayData.drugDuration}
        drugFormFlexStyle={Object.values(hideDrugForm).some(Boolean)}
        drugFrequencies={frequencyOptions}
        drugPermission={props.drugPermission}
        drugPrn={props.drugPrn}
        drugQty={displayData.drugQty}
        drugRoutes={displayData.DrugSelectSequence?.drugRoutes}
        drugSites={displayData.DrugSelectSequence?.drugSites}
        drugUnits={displayData.DrugSelectSequence?.drugUnits}
        enabledButtonDispense={displayData.DrugSelectSequence?.is_dispensed}
        errorPediatricData={errorPediatricData}
        hasFactSheet={Object.keys(props.selectedDrug?.original?.factsheet_data || {}).length > 0}
        hideDrugForm={hideDrugForm}
        hideSolvent={!displayData.modSolventSelectionConfig?.activeButtonSolvent}
        isRequireRate={isRequireRate}
        languageUX={props.languageUX}
        modSolventSelectionConfig={displayData.modSolventSelectionConfig}
        openCalCulator={props.openCalculator}
        openFactSheet={handleOpenFactSheet}
        priority={priority}
        readOnlyDuration={props.orderType?.includes("ONE") || props.orderType?.includes("STAT")}
        selectedDrug={props.selectedDrug}
        selectedDrugFrequencyId={displayData.DrugSelectSequence?.selectedDrugFrequencyId}
        selectedDrugRouteId={displayData.DrugSelectSequence?.selectedDrugRouteId}
        selectedDrugSiteId={displayData.DrugSelectSequence?.selectedDrugSiteId}
        selectedDrugUnitId={displayData.DrugSelectSequence?.selectedDrugUnitId}
        showHeader={props.showHeader}
        statusRequest={props.statusRequested}
        errorMessage={props.errorMessage}
        priorityOptions={priorityOptions}
        // prnOptions={prnOptions}
        unitOptions={props.masterOptions?.unit || []}
        // onAdditionPrn={handleAddPrn}
        onChangeModData={handleChangeModData}
        onClickDispense={onClickDispense}
        onClose={handleClose}
        onCloseButtonSolvent={handleCloseButtonSolvent}
        onOpenModSolventSelection={handleOpenModSolventSelection}
        buttonAddToOrder={
          <Button
            id="btn-addMedOrder"
            color={isEditable ? "yellow" : "green"}
            disabled={!props.statusRequested}
            style={{ minWidth: "max-content" }}
            fluid
            onClick={handleAddToOrder}
          >
            {isEditable ? "EDIT ORDER" : "ADD TO ORDER"}
          </Button>
        }
        buttonTaperSchedule={
          <Popup
            position="bottom right"
            pinned
            on="click"
            content={
              <TaperPopup
                duration={displayData.drugDuration}
                startDate={displayData.DrugSelectSequence?.selectedDate}
                onChangeDate={handleChangeDate}
              />
            }
            trigger={
              <Button
                color="purple"
                size="small"
                style={{
                  alignItems: "center",
                  background: "#9a4082",
                  display: "flex",
                  marginLeft: "0.5rem",
                  padding: "0.55em 0.75em 0.55rem 0.5rem",
                  width: "9.75rem",
                }}
              >
                <img
                  alt="taper-schedule.icon"
                  src={"/static/images/taper-schedule.png"}
                  style={{ width: "1.5rem" }}
                />
                <span style={{ marginLeft: "10px" }}>Taper Schedule</span>
              </Button>
            }
          />
        }
        calculatorIcon={
          <CalculatorIcon
            enabledCalculatorButton={enabledCalculatorButton}
            openCalCulator={props.openCalculator}
            setOpenCalculator={props.setOpenCalculator}
          />
        }
        doseInput={
          <Input
            id="txt-drugDose"
            disabled={!props.selectedDrug}
            style={{ opacity: 1 }}
            type="number"
            value={displayData.drugDose || ""}
            fluid
            onBlur={handleBlurDrugDose}
            onChange={handleChangeDrugDose}
          />
        }
        dripDurationLabel={
          <CustomRadioButton
            checked={displayData.modSolventSelectionConfig?.dripRate === "drip"}
            label="Drip Duration"
            name="dripRate"
            showHeader={props.showHeader}
            value="drip"
            onChange={handleChangeModData}
          />
        }
        dripRateLabel={
          <CustomRadioButton
            checked={displayData.modSolventSelectionConfig?.dripRate === "rate"}
            label="Drip Rate"
            name="dripRate"
            showHeader={props.showHeader}
            value="rate"
            onChange={handleChangeModData}
          />
        }
        DrugSearchBox={
          <DrugSearchBox
            onEvent={props.onEvent}
            setProp={props.setProp}
            disabled={!props.statusRequested || (isEditable && props.showHeader)}
            drugSearchLoading={props.drugSearchLoading}
            drugSearchResult={props.drugSearchResult}
            drugSearchText={displayData.drugSearchText}
            selectedDrug={props.selectedDrug}
            errorMessage={props.errorMessage}
            onSelect={handleSelectDrug}
          />
        }
        noRequiredDuration={[
          "ONE_DAY_IPD",
          "ONE_DOSE_IPD",
          "ONE_DOSE_OPD",
          "STAT_IPD",
          "STAT_OPD",
        ].includes(props.orderType || "")}
        pediatricUI={
          props.openCalculator ? (
            <ModPediatricDose
              onEvent={props.onEvent}
              setProp={props.setProp}
              drugDetail={props.selectedDrug?.original}
              open={props.openCalculator}
              pediatricData={props.pediatricData}
              // callback
              onClose={handleCloseModPediatricDose}
              languageUX={props.languageUX}
            />
          ) : null
        }
        pediatricWarning={
          <div
            style={{
              background: "#ffe8e6",
              color: "red",
              display: "flex",
              padding: "10px",
              width: "fit-content",
            }}
          >
            {errorPediatricData?.map((item: any, index: number) => (
              <div
                key={`error-${index}`}
                dangerouslySetInnerHTML={{ __html: item }}
                style={{ paddingRight: "10px" }}
              />
            ))}
          </div>
        }
        prnDropdown={
          <Dropdown
            className="inline-label"
            disabled={!props.selectedDrug}
            onAddItem={handleAddPrn}
            onChange={handleChangeDrugPrn}
            options={prnOptions}
            selectOnBlur={false}
            allowAdditions
            clearable
            fluid
            search
            selection
            style={{ width: "100%" }}
            value={props.drugPrn ? props.drugPrn : ""}
          />
        }
        searchBoxSolvent={
          <SearchBoxDropdown
            onEvent={props.onEvent}
            id="EMO"
            icon="search"
            limit={20}
            selectedItem={displayData.modSolventSelectionConfig?.solvent || null}
            style={{ width: "100%" }}
            // config
            type="Drug"
            fluid
            inline
            useWithKey
            searchedItemListWithKey={displayData.searchedItemListWithKey}
            setSelectedItem={handleSelectedItem}
            onAdditionalUrlParams={(searchQuery) => ({
              keyword: undefined,
              name: searchQuery,
              solvent_for: props.selectedDrug?.id,
            })}
          />
        }
        showPriority={
          props.orderTypeTab === DRUG_ORDER_TYPE.DAILY && !!props.DrugSelectSequence?.priority
        }
      />
      <ModDrugFact
        drugDetail={props.selectedDrug?.original}
        open={openModFactSheet}
        // callback
        onClose={handleCloseModFactSheet}
        languageUX={props.languageUX}
      />
      <ModDrugDispensingOrder
        frequency={drugDispensingFrequency}
        fullName={displayData.DrugSelectSequence?.full_name}
        languageUX={props.languageUX}
        open={openModDispense}
        route={displayData.DrugSelectSequence?.default_route}
        totalDispenseUnit={displayData.DrugSelectSequence?.total_dispense_unit}
        unit={displayData.DrugSelectSequence?.dispense_unit}
        frequencyOptions={frequencyOptions}
        routeOptions={displayData.DrugSelectSequence?.drugRoutes}
        // options
        unitOptions={props.masterOptions?.unit || []}
        // callback
        onClose={handleCloseModDispense}
        onSave={handleSaveDrugDispense}
        disabledFrequency={
          props.orderTypeTab === DRUG_ORDER_TYPE.DAILY && props.orderType?.includes("STAT")
        }
      />
    </div>
  );
};

/* ------------------------------------------------------ */

/*                     CalculatorIcon                     */

/* ------------------------------------------------------ */
type CalculatorIconProps = {
  enabledCalculatorButton: boolean;
  openCalCulator: boolean;
  setOpenCalculator: (value: boolean) => void;
};

export const CalculatorIcon = (props: CalculatorIconProps) =>
  !CONFIG.HIDE_DOSE_CALCULATOR && (
    <div
      aria-hidden="true"
      style={{ whiteSpace: "nowrap" }}
      onClick={() => {
        if (props.enabledCalculatorButton) {
          props.setOpenCalculator(!props.openCalCulator);
        }
      }}
    >
      <svg
        fill="none"
        height="44"
        viewBox="0 0 49 44"
        width="49"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>Calculator button</title>
        <path
          d="M7.94727 38.6416H9.40723C9.37793 39.1201 9.24609 39.5449 9.01172 39.916C8.7806 40.2871 8.45671 40.5768 8.04004 40.7852C7.62663 40.9935 7.12858 41.0977 6.5459 41.0977C6.09017 41.0977 5.68164 41.0195 5.32031 40.8633C4.95898 40.7038 4.64974 40.4759 4.39258 40.1797C4.13867 39.8835 3.94499 39.5254 3.81152 39.1055C3.67806 38.6855 3.61133 38.2152 3.61133 37.6943V37.2012C3.61133 36.6803 3.67969 36.21 3.81641 35.79C3.95638 35.3669 4.15495 35.0072 4.41211 34.7109C4.67253 34.4147 4.9834 34.1868 5.34473 34.0273C5.70605 33.8678 6.1097 33.7881 6.55566 33.7881C7.14811 33.7881 7.64779 33.8955 8.05469 34.1104C8.46484 34.3252 8.78223 34.6214 9.00684 34.999C9.2347 35.3766 9.37142 35.8063 9.41699 36.2881H7.95215C7.93587 36.0016 7.87891 35.7591 7.78125 35.5605C7.68359 35.3587 7.53548 35.2074 7.33691 35.1064C7.1416 35.0023 6.88118 34.9502 6.55566 34.9502C6.31152 34.9502 6.09831 34.9958 5.91602 35.0869C5.73372 35.1781 5.58073 35.3164 5.45703 35.502C5.33333 35.6875 5.24056 35.9219 5.17871 36.2051C5.12012 36.485 5.09082 36.8138 5.09082 37.1914V37.6943C5.09082 38.0622 5.11849 38.3861 5.17383 38.666C5.22917 38.9427 5.3138 39.1771 5.42773 39.3691C5.54492 39.5579 5.69466 39.7012 5.87695 39.7988C6.0625 39.8932 6.28548 39.9404 6.5459 39.9404C6.85189 39.9404 7.10417 39.8916 7.30273 39.7939C7.5013 39.6963 7.65267 39.5514 7.75684 39.3594C7.86426 39.1673 7.92773 38.9281 7.94727 38.6416ZM13.1328 39.8086V37.4551C13.1328 37.2858 13.1051 37.141 13.0498 37.0205C12.9945 36.8968 12.9082 36.8008 12.791 36.7324C12.6771 36.6641 12.529 36.6299 12.3467 36.6299C12.1904 36.6299 12.0553 36.6576 11.9414 36.7129C11.8275 36.765 11.7396 36.8415 11.6777 36.9424C11.6159 37.04 11.585 37.1556 11.585 37.2891H10.1787C10.1787 37.0645 10.2308 36.8512 10.335 36.6494C10.4391 36.4476 10.5905 36.2702 10.7891 36.1172C10.9876 35.9609 11.2236 35.8389 11.4971 35.751C11.7738 35.6631 12.083 35.6191 12.4248 35.6191C12.835 35.6191 13.1995 35.6875 13.5186 35.8242C13.8376 35.9609 14.0882 36.166 14.2705 36.4395C14.4561 36.7129 14.5488 37.0547 14.5488 37.4648V39.7256C14.5488 40.0153 14.5667 40.2529 14.6025 40.4385C14.6383 40.6208 14.6904 40.7803 14.7588 40.917V41H13.3379C13.2695 40.8568 13.2174 40.6777 13.1816 40.4629C13.1491 40.2448 13.1328 40.0267 13.1328 39.8086ZM13.3184 37.7822L13.3281 38.5781H12.542C12.3564 38.5781 12.1953 38.5993 12.0586 38.6416C11.9219 38.6839 11.8096 38.7441 11.7217 38.8223C11.6338 38.8971 11.5687 38.985 11.5264 39.0859C11.4873 39.1868 11.4678 39.2975 11.4678 39.418C11.4678 39.5384 11.4954 39.6475 11.5508 39.7451C11.6061 39.8395 11.6859 39.9144 11.79 39.9697C11.8942 40.0218 12.0163 40.0479 12.1562 40.0479C12.3678 40.0479 12.5518 40.0055 12.708 39.9209C12.8643 39.8363 12.9847 39.7321 13.0693 39.6084C13.1572 39.4847 13.2028 39.3675 13.2061 39.2568L13.5771 39.8525C13.5251 39.986 13.4535 40.1243 13.3623 40.2676C13.2744 40.4108 13.1621 40.5459 13.0254 40.6729C12.8887 40.7965 12.7243 40.8991 12.5322 40.9805C12.3402 41.0586 12.1123 41.0977 11.8486 41.0977C11.5133 41.0977 11.209 41.0309 10.9355 40.8975C10.6654 40.7607 10.4505 40.5736 10.291 40.3359C10.1348 40.0951 10.0566 39.8216 10.0566 39.5156C10.0566 39.2389 10.1087 38.9932 10.2129 38.7783C10.3171 38.5635 10.4701 38.3828 10.6719 38.2363C10.877 38.0866 11.1325 37.9743 11.4385 37.8994C11.7445 37.8213 12.0993 37.7822 12.5029 37.7822H13.3184ZM17.1172 33.5V41H15.7061V33.5H17.1172ZM18.3477 40.3213C18.3477 40.113 18.4193 39.9388 18.5625 39.7988C18.709 39.6589 18.9027 39.5889 19.1436 39.5889C19.3844 39.5889 19.5765 39.6589 19.7197 39.7988C19.8662 39.9388 19.9395 40.113 19.9395 40.3213C19.9395 40.5296 19.8662 40.7038 19.7197 40.8438C19.5765 40.9837 19.3844 41.0537 19.1436 41.0537C18.9027 41.0537 18.709 40.9837 18.5625 40.8438C18.4193 40.7038 18.3477 40.5296 18.3477 40.3213ZM25.9404 41H24.3926L24.4023 39.8574H25.9404C26.3245 39.8574 26.6484 39.7712 26.9121 39.5986C27.1758 39.4229 27.3743 39.1673 27.5078 38.832C27.6445 38.4967 27.7129 38.0915 27.7129 37.6162V37.2695C27.7129 36.9049 27.6738 36.5843 27.5957 36.3076C27.5208 36.0309 27.4085 35.7982 27.2588 35.6094C27.109 35.4206 26.9251 35.279 26.707 35.1846C26.4889 35.0869 26.2383 35.0381 25.9551 35.0381H24.3633V33.8906H25.9551C26.4303 33.8906 26.8649 33.972 27.2588 34.1348C27.6559 34.2943 27.9993 34.5238 28.2891 34.8232C28.5788 35.1227 28.8018 35.4808 28.958 35.8975C29.1175 36.3109 29.1973 36.7715 29.1973 37.2793V37.6162C29.1973 38.1208 29.1175 38.5814 28.958 38.998C28.8018 39.4147 28.5788 39.7728 28.2891 40.0723C28.0026 40.3685 27.6592 40.598 27.2588 40.7607C26.8617 40.9202 26.4222 41 25.9404 41ZM25.2227 33.8906V41H23.7578V33.8906H25.2227ZM29.9492 38.4121V38.3096C29.9492 37.9222 30.0046 37.5658 30.1152 37.2402C30.2259 36.9115 30.387 36.6266 30.5986 36.3857C30.8102 36.1449 31.0706 35.9577 31.3799 35.8242C31.6891 35.6875 32.0439 35.6191 32.4443 35.6191C32.8447 35.6191 33.2012 35.6875 33.5137 35.8242C33.8262 35.9577 34.0882 36.1449 34.2998 36.3857C34.5146 36.6266 34.6774 36.9115 34.7881 37.2402C34.8988 37.5658 34.9541 37.9222 34.9541 38.3096V38.4121C34.9541 38.7962 34.8988 39.1527 34.7881 39.4814C34.6774 39.807 34.5146 40.0918 34.2998 40.3359C34.0882 40.5768 33.8278 40.764 33.5186 40.8975C33.2093 41.0309 32.8545 41.0977 32.4541 41.0977C32.0537 41.0977 31.6973 41.0309 31.3848 40.8975C31.0755 40.764 30.8135 40.5768 30.5986 40.3359C30.387 40.0918 30.2259 39.807 30.1152 39.4814C30.0046 39.1527 29.9492 38.7962 29.9492 38.4121ZM31.3555 38.3096V38.4121C31.3555 38.6335 31.375 38.8402 31.4141 39.0322C31.4531 39.2243 31.515 39.3936 31.5996 39.54C31.6875 39.6833 31.8014 39.7956 31.9414 39.877C32.0814 39.9583 32.2523 39.999 32.4541 39.999C32.6494 39.999 32.8171 39.9583 32.957 39.877C33.097 39.7956 33.2093 39.6833 33.2939 39.54C33.3786 39.3936 33.4404 39.2243 33.4795 39.0322C33.5218 38.8402 33.543 38.6335 33.543 38.4121V38.3096C33.543 38.0947 33.5218 37.8929 33.4795 37.7041C33.4404 37.512 33.377 37.3428 33.2891 37.1963C33.2044 37.0465 33.0921 36.9294 32.9521 36.8447C32.8122 36.7601 32.6429 36.7178 32.4443 36.7178C32.2458 36.7178 32.0765 36.7601 31.9365 36.8447C31.7998 36.9294 31.6875 37.0465 31.5996 37.1963C31.515 37.3428 31.4531 37.512 31.4141 37.7041C31.375 37.8929 31.3555 38.0947 31.3555 38.3096ZM38.6748 39.54C38.6748 39.4391 38.6455 39.348 38.5869 39.2666C38.5283 39.1852 38.4193 39.1104 38.2598 39.042C38.1035 38.9704 37.8773 38.9053 37.5811 38.8467C37.3141 38.7881 37.0651 38.7148 36.834 38.627C36.6061 38.5358 36.4076 38.4268 36.2383 38.2998C36.0723 38.1729 35.9421 38.0231 35.8477 37.8506C35.7533 37.6748 35.7061 37.4746 35.7061 37.25C35.7061 37.0286 35.7533 36.8203 35.8477 36.625C35.9453 36.4297 36.0837 36.2572 36.2627 36.1074C36.445 35.9544 36.6663 35.8356 36.9268 35.751C37.1904 35.6631 37.4867 35.6191 37.8154 35.6191C38.2744 35.6191 38.6683 35.6924 38.9971 35.8389C39.3291 35.9854 39.583 36.1872 39.7588 36.4443C39.9378 36.6982 40.0273 36.988 40.0273 37.3135H38.6211C38.6211 37.1768 38.5918 37.0547 38.5332 36.9473C38.4779 36.8366 38.39 36.7503 38.2695 36.6885C38.1523 36.6234 37.9993 36.5908 37.8105 36.5908C37.6543 36.5908 37.5192 36.6185 37.4053 36.6738C37.2913 36.7259 37.2035 36.7975 37.1416 36.8887C37.083 36.9766 37.0537 37.0742 37.0537 37.1816C37.0537 37.263 37.07 37.3363 37.1025 37.4014C37.1383 37.4632 37.1953 37.5202 37.2734 37.5723C37.3516 37.6243 37.4525 37.6732 37.5762 37.7188C37.7031 37.7611 37.8594 37.8001 38.0449 37.8359C38.4258 37.9141 38.766 38.0166 39.0654 38.1436C39.3649 38.2673 39.6025 38.4365 39.7783 38.6514C39.9541 38.863 40.042 39.1413 40.042 39.4863C40.042 39.7207 39.9899 39.9355 39.8857 40.1309C39.7816 40.3262 39.6318 40.4971 39.4365 40.6436C39.2412 40.7868 39.0068 40.8991 38.7334 40.9805C38.4632 41.0586 38.1589 41.0977 37.8203 41.0977C37.3288 41.0977 36.9121 41.0098 36.5703 40.834C36.2318 40.6582 35.9746 40.4352 35.7988 40.165C35.6263 39.8916 35.54 39.6117 35.54 39.3252H36.873C36.8796 39.5173 36.9284 39.6719 37.0195 39.7891C37.1139 39.9062 37.2327 39.9909 37.376 40.043C37.5225 40.0951 37.6803 40.1211 37.8496 40.1211C38.0319 40.1211 38.1833 40.0967 38.3037 40.0479C38.4242 39.9958 38.5153 39.9274 38.5771 39.8428C38.6423 39.7549 38.6748 39.654 38.6748 39.54ZM43.3867 41.0977C42.9766 41.0977 42.6087 41.0326 42.2832 40.9023C41.9577 40.7689 41.681 40.585 41.4531 40.3506C41.2285 40.1162 41.056 39.8444 40.9355 39.5352C40.8151 39.2227 40.7549 38.8906 40.7549 38.5391V38.3438C40.7549 37.9434 40.8118 37.5771 40.9258 37.2451C41.0397 36.9131 41.2025 36.625 41.4141 36.3809C41.6289 36.1367 41.8893 35.9495 42.1953 35.8193C42.5013 35.6859 42.8464 35.6191 43.2305 35.6191C43.6048 35.6191 43.9368 35.681 44.2266 35.8047C44.5163 35.9284 44.7588 36.1042 44.9541 36.332C45.1527 36.5599 45.3024 36.8333 45.4033 37.1523C45.5042 37.4681 45.5547 37.8197 45.5547 38.207V38.793H41.3555V37.8555H44.1729V37.748C44.1729 37.5527 44.137 37.3786 44.0654 37.2256C43.9971 37.0693 43.8929 36.9456 43.7529 36.8545C43.613 36.7633 43.4339 36.7178 43.2158 36.7178C43.0303 36.7178 42.8708 36.7585 42.7373 36.8398C42.6038 36.9212 42.4948 37.0352 42.4102 37.1816C42.3288 37.3281 42.2669 37.5007 42.2246 37.6992C42.1855 37.8945 42.166 38.1094 42.166 38.3438V38.5391C42.166 38.7507 42.1953 38.946 42.2539 39.125C42.3158 39.304 42.402 39.4587 42.5127 39.5889C42.6266 39.7191 42.7633 39.82 42.9229 39.8916C43.0856 39.9632 43.2695 39.999 43.4746 39.999C43.7285 39.999 43.9645 39.9502 44.1826 39.8525C44.404 39.7516 44.5944 39.6003 44.7539 39.3984L45.4375 40.1406C45.3268 40.3001 45.1755 40.4531 44.9834 40.5996C44.7946 40.7461 44.5667 40.8665 44.2998 40.9609C44.0329 41.0521 43.7285 41.0977 43.3867 41.0977Z"
          fill={props.enabledCalculatorButton ? "#0072BC" : "#dddddd"}
        />
        <g clipPath="url(#clip0_848_9)">
          <path
            d="M12.75 30.322H25.8125C25.125 29.5166 24.375 29.0001 24.375 27.072C24.375 25.144 31 19.0001 32 20.0001V5.57202C32 4.84268 31.7103 4.1432 31.1945 3.62748C30.6788 3.11175 29.9793 2.82202 29.25 2.82202H12.75C12.0207 2.82202 11.3212 3.11175 10.8055 3.62748C10.2897 4.1432 10 4.84268 10 5.57202V27.572C10 28.3014 10.2897 29.0008 10.8055 29.5166C11.3212 30.0323 12.0207 30.322 12.75 30.322ZM16.875 26.197H14.125V23.447H16.875V26.197ZM16.875 20.697H14.125V17.947H16.875V20.697ZM16.875 15.197H14.125V12.447H16.875V15.197ZM22.375 26.197H19.625V23.447H22.375V26.197ZM22.375 20.697H19.625V17.947H22.375V20.697ZM22.375 15.197H19.625V12.447H22.375V15.197ZM27.875 22.0337L25.125 25.0001V17.947H27.875V22.0337ZM27.875 15.197H25.125V12.447H27.875V15.197ZM12.75 5.57202H29.25V9.69702H12.75V5.57202Z"
            fill={props.enabledCalculatorButton ? "#0072BC" : "#dddddd"}
          />
          <path
            d="M33.4501 22.0417C32.8667 21.4584 32.1084 21.1667 31.4084 21.1667C30.7084 21.1667 29.8917 21.4584 29.3667 22.0417L25.9834 25.3084C24.8167 26.4751 24.8167 28.2834 25.9834 29.4501C27.1501 30.6167 28.9584 30.6167 30.1251 29.4501L33.4501 26.1251C34.5584 25.0167 34.5584 23.1501 33.4501 22.0417ZM32.6334 25.3084L31.0001 26.9417L28.9001 24.9001L26.3334 27.4667C26.3334 27.0001 26.4501 26.4751 26.8584 26.1251L30.1834 22.8001C30.4751 22.5084 30.9417 22.3334 31.3501 22.3334C31.7584 22.3334 32.2251 22.5084 32.5751 22.8001C33.2751 23.5584 33.2751 24.6084 32.6334 25.3084ZM35.4334 24.1417C35.4334 24.6084 35.3167 25.0167 35.2001 25.4834C35.7834 26.1834 35.7834 27.2334 35.1417 27.8751L33.5084 29.5084L32.6334 28.6334L31.0001 30.2667C30.2417 31.0251 29.1917 31.4334 28.2001 31.4334C28.3167 31.6084 28.4334 31.7834 28.6084 31.9584C29.7751 33.1251 31.5834 33.1251 32.7501 31.9584L36.0751 28.6334C37.2417 27.4667 37.2417 25.6584 36.0751 24.4917C35.7834 24.3751 35.6084 24.2584 35.4334 24.1417Z"
            fill={props.enabledCalculatorButton ? "#0072BC" : "#dddddd"}
          />
        </g>
        <defs>
          <clipPath id="clip0_848_9">
            <rect fill="white" height="33" transform="translate(8)" width="33" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );

/* ------------------------------------------------------ */

/*                   CustomRadioButton;                   */

/* ------------------------------------------------------ */
type CustomRadioButtonProps = {
  checked: boolean;
  label: string;
  labelStyle?: CSSProperties;
  name: string;
  showHeader?: boolean;
  value: string;
  onChange: (e: null, data: { checked: boolean; name: string; value: string }) => void;
};

export const CustomRadioButton = (props: CustomRadioButtonProps) => {
  const handleChange = () => {
    props.onChange(null, { checked: !props.checked, name: props.name, value: props.value });
  };

  return (
    <FormField style={{ display: "flex" }} onClick={handleChange}>
      <Radio
        checked={props.checked}
        style={{
          marginBottom: props.showHeader ? "" : "-2rem",
          marginRight: "0.25rem",
          transform: "scale(0.85)",
        }}
      />
      <label
        htmlFor={props.name}
        style={{
          cursor: "pointer",
          marginTop: "0.15rem",
          minWidth: "max-content",
          ...props.labelStyle,
        }}
      >
        {props.label}
      </label>
    </FormField>
  );
};

export default React.memo(CardEditMedOrder);
